/**
 * Repositorio de eventos - Gestión CRUD en Firestore
 * Usado por la landing (solo lectura) y el panel (CRUD completo)
 */
import { db } from '../firebase/firestore.js';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';

const EVENTS_COLLECTION = 'events';

// ============================================
// MÉTODOS DE LECTURA (Landing + Panel)
// ============================================

/**
 * Listar todos los eventos públicos (para landing pública)
 * @returns {Promise<Array>} Lista de eventos públicos
 */
export const listPublicEvents = async () => {
  try {
    // Nota: Sin índice compuesto, hacemos la consulta sin orderBy en Firestore
    // y ordenamos en cliente. Para producción, crear índice compuesto:
    // isPublico (Ascending) + createdAt (Descending)
    const q = query(
      collection(db, EVENTS_COLLECTION),
      where('isPublico', '==', true)
    );

    const querySnapshot = await getDocs(q);
    const events = [];
    querySnapshot.forEach((docSnap) => {
      events.push({ id: docSnap.id, ...docSnap.data() });
    });
    
    // Ordenar en cliente por createdAt (descendente)
    events.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
      const dateB = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
      return dateB - dateA;
    });
    
    return events;
  } catch (error) {
    console.error("Error al obtener eventos públicos:", error);
    throw error;
  }
};

/**
 * Obtener un evento por su ID
 * @param {string} eventId - ID del evento
 * @returns {Promise<Object|null>} Evento o null si no existe
 */
export const getEvent = async (eventId) => {
  try {
    const docRef = doc(db, EVENTS_COLLECTION, eventId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error al obtener evento:", error);
    throw error;
  }
};

// ============================================
// MÉTODOS DEL PANEL (Solo usuarios autenticados)
// ============================================

/**
 * Listar eventos del usuario actual (para panel)
 * @param {string} uid - UID del usuario autenticado
 * @returns {Promise<Array>} Lista de eventos del usuario
 */
export const listMyEvents = async (uid) => {
  try {
    // Nota: Sin índice compuesto, hacemos la consulta sin orderBy en Firestore
    // y ordenamos en cliente. Para producción, crear índice compuesto:
    // ownerUid (Ascending) + createdAt (Descending)
    const q = query(
      collection(db, EVENTS_COLLECTION),
      where('ownerUid', '==', uid)
    );

    const querySnapshot = await getDocs(q);
    const events = [];
    querySnapshot.forEach((docSnap) => {
      events.push({ id: docSnap.id, ...docSnap.data() });
    });
    
    // Ordenar en cliente por createdAt (descendente)
    events.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
      const dateB = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
      return dateB - dateA;
    });
    
    return events;
  } catch (error) {
    console.error("Error al obtener mis eventos:", error);
    throw error;
  }
};

/**
 * Crear un nuevo evento
 * @param {Object} payload - Datos del evento
 * @param {string} payload.titulo - Título del evento (requerido)
 * @param {string} payload.descripcion - Descripción del evento
 * @param {Date} payload.fechaInicio - Fecha de inicio
 * @param {Date} payload.fechaFin - Fecha de fin
 * @param {boolean} payload.isPublico - Si el evento es público
 * @param {string} payload.imagenUrl - URL de la imagen (opcional)
 * @param {string} payload.ownerUid - UID del propietario (requerido)
 * @returns {Promise<Object>} Evento creado con su ID
 */
export const createEvent = async (payload) => {
  try {
    // Validación básica en cliente (las reglas Firestore validan en servidor)
    if (!payload.titulo || !payload.ownerUid) {
      throw new Error('Título y ownerUid son requeridos');
    }

    const eventData = {
      titulo: payload.titulo,
      descripcion: payload.descripcion || '',
      fechaInicio: payload.fechaInicio || null,
      fechaFin: payload.fechaFin || null,
      isPublico: payload.isPublico ?? false,
      imagenUrl: payload.imagenUrl || 'https://placehold.co/400x200?text=Evento',
      ownerUid: payload.ownerUid,
      isCerrado: payload.isCerrado ?? false,
      accessMode: 'none', // "none" | "key-ui"
      accessKeyHash: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, EVENTS_COLLECTION), eventData);
    return { id: docRef.id, ...eventData };
  } catch (error) {
    console.error("Error al crear evento:", error);
    throw error;
  }
};

/**
 * Actualizar un evento existente
 * NOTA: Las reglas de Firestore deben validar que el usuario sea el owner
 * @param {string} eventId - ID del evento a actualizar
 * @param {Object} payload - Campos a actualizar
 * @returns {Promise<void>}
 */
export const updateEvent = async (eventId, payload) => {
  try {
    const docRef = doc(db, EVENTS_COLLECTION, eventId);

    // Filtrar campos válidos para actualizar
    const updateData = {};
    const allowedFields = [
      'titulo', 'descripcion', 'fechaInicio', 'fechaFin',
      'isPublico', 'imagenUrl', 'accessMode', 'isCerrado'
    ];

    allowedFields.forEach(field => {
      if (payload[field] !== undefined) {
        updateData[field] = payload[field];
      }
    });

    updateData.updatedAt = serverTimestamp();

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error("Error al actualizar evento:", error);
    throw error;
  }
};

/**
 * Eliminar un evento
 * NOTA: Las reglas de Firestore deben validar que el usuario sea el owner
 * @param {string} eventId - ID del evento a eliminar
 * @returns {Promise<void>}
 */
export const deleteEvent = async (eventId) => {
  try {
    const docRef = doc(db, EVENTS_COLLECTION, eventId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error al eliminar evento:", error);
    throw error;
  }
};

/**
 * Establecer clave de acceso para un evento (modo key-ui)
 * ADVERTENCIA: Esta es una barrera de UX, NO seguridad real.
 * El hash se almacena en Firestore y se verifica en el cliente.
 * Para seguridad real, se requiere validación en backend/Cloud Functions.
 * 
 * @param {string} eventId - ID del evento
 * @param {string|null} keyHash - Hash SHA-256 de la clave o null para desactivar
 * @returns {Promise<void>}
 */
export const setAccessKey = async (eventId, keyHash) => {
  try {
    const docRef = doc(db, EVENTS_COLLECTION, eventId);
    await updateDoc(docRef, {
      accessMode: keyHash ? 'key-ui' : 'none',
      accessKeyHash: keyHash,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error("Error al establecer clave de acceso:", error);
    throw error;
  }
};
